export type KeywordAdProduct = 'SP' | 'SB';

export type KeywordEntityRow = {
  keyword_id?: string | number;
  keyword_text?: string;
  match_type?: string;
  state?: string;
  bid?: number;
  campaign_id?: string | number;
  ad_group_id?: string | number;
  extended_data?: {
    serving_status?: string;
    serving_status_details?: string[];
  } | null;
};

export type UnifiedKeywordSource = KeywordEntityRow & {
  adProduct: KeywordAdProduct;
};

export type UnifiedKeywordRow = {
  groupKey: string;
  keyword_text: string;
  match_type: string;
  match_type_abbrev: string;
  campaign_types: string;
  keyword_id: string;
  bid: string;
  campaign_id: string;
  campaign_label: string;
  ad_group_id: string;
  state: string;
  serving_status: string;
  sources: UnifiedKeywordSource[];
};

export function coerceDisplayString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return String(value).trim();
}

export function normalizeMatchTypeForGroup(matchType: string | undefined): string {
  const raw = coerceDisplayString(matchType).toUpperCase();
  return raw || 'UNKNOWN';
}

export function normalizeKeywordTextForGroup(text: string | undefined): string {
  return coerceDisplayString(text).toLowerCase();
}

export function normalizeKeywordKey(text: string | undefined, matchType: string | undefined): string {
  return `${normalizeKeywordTextForGroup(text)}|${normalizeMatchTypeForGroup(matchType)}`;
}

export function matchTypeAbbrev(matchType: string | undefined): string {
  switch (normalizeMatchTypeForGroup(matchType)) {
    case 'EXACT':
      return 'E';
    case 'PHRASE':
      return 'P';
    case 'BROAD':
      return 'B';
    default:
      return '-';
  }
}

export function formatCampaignTypes(flags: { sp: boolean; sb: boolean }): string {
  if (flags.sp && flags.sb) return 'SP, SB';
  if (flags.sp) return 'SP';
  if (flags.sb) return 'SB';
  return '-';
}

function uniqueNonEmpty(values: unknown[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const t = coerceDisplayString(v);
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

function summarizeField(values: unknown[]): string {
  const unique = uniqueNonEmpty(values);
  if (unique.length === 0) return '-';
  if (unique.length === 1) return unique[0];
  return `Multiple (${unique.length})`;
}

function summarizeBid(sources: UnifiedKeywordSource[]): string {
  const bids = sources
    .map((s) => s.bid)
    .filter((b): b is number => typeof b === 'number' && !Number.isNaN(b));
  if (bids.length === 0) return '-';
  if (bids.length === 1) return String(bids[0]);
  const min = Math.min(...bids);
  const max = Math.max(...bids);
  if (min === max) return String(min);
  return `Multiple (${bids.length})`;
}

function buildUnifiedRow(
  groupKey: string,
  keywordText: string,
  matchType: string,
  sources: UnifiedKeywordSource[]
): UnifiedKeywordRow {
  const sp = sources.some((s) => s.adProduct === 'SP');
  const sb = sources.some((s) => s.adProduct === 'SB');
  const displayText =
    coerceDisplayString(
      sources.find((s) => coerceDisplayString(s.keyword_text))?.keyword_text
    ) ||
    keywordText ||
    '-';
  const displayMatchType =
    coerceDisplayString(
      sources.find((s) => coerceDisplayString(s.match_type))?.match_type
    ) ||
    matchType ||
    '-';

  const keywordIds = sources.map((s) => s.keyword_id);
  const campaignIds = sources.map((s) => s.campaign_id);
  const adGroupIds = sources.map((s) => s.ad_group_id);
  const states = sources.map((s) => s.state);
  const servingStatuses = sources.map((s) => s.extended_data?.serving_status);

  return {
    groupKey,
    keyword_text: displayText,
    match_type: displayMatchType,
    match_type_abbrev: matchTypeAbbrev(displayMatchType),
    campaign_types: formatCampaignTypes({ sp, sb }),
    keyword_id: summarizeField(keywordIds),
    bid: summarizeBid(sources),
    campaign_id: summarizeField(campaignIds),
    campaign_label: summarizeField(campaignIds),
    ad_group_id: summarizeField(adGroupIds),
    state: summarizeField(states),
    serving_status: summarizeField(servingStatuses),
    sources,
  };
}

export function mergeUnifiedKeywords(
  spRows: KeywordEntityRow[],
  sbRows: KeywordEntityRow[]
): UnifiedKeywordRow[] {
  const buckets = new Map<string, UnifiedKeywordSource[]>();

  const add = (row: KeywordEntityRow, adProduct: KeywordAdProduct) => {
    const key = normalizeKeywordKey(row.keyword_text, row.match_type);
    const list = buckets.get(key) || [];
    list.push({ ...row, adProduct });
    buckets.set(key, list);
  };

  for (const row of spRows) add(row, 'SP');
  for (const row of sbRows) add(row, 'SB');

  const rows: UnifiedKeywordRow[] = [];
  for (const [groupKey, sources] of buckets) {
    const [textPart, matchPart] = groupKey.split('|');
    const keywordText =
      coerceDisplayString(
        sources.find((s) => normalizeKeywordTextForGroup(s.keyword_text) === textPart)?.keyword_text
      ) ||
      textPart ||
      '-';
    const matchType =
      coerceDisplayString(
        sources.find((s) => normalizeMatchTypeForGroup(s.match_type) === matchPart)?.match_type
      ) ||
      matchPart ||
      '-';
    rows.push(buildUnifiedRow(groupKey, keywordText, matchType, sources));
  }

  rows.sort((a, b) => {
    const textCmp = a.keyword_text.localeCompare(b.keyword_text, undefined, { sensitivity: 'base' });
    if (textCmp !== 0) return textCmp;
    return a.match_type.localeCompare(b.match_type, undefined, { sensitivity: 'base' });
  });

  return rows;
}

export function resolveCampaignDisplayName(
  campaignId: unknown,
  campaignNameById: Map<string, string>
): string {
  const id = coerceDisplayString(campaignId);
  if (!id) return '';
  return campaignNameById.get(id) ?? campaignNameById.get(String(id)) ?? id;
}

/** Unique campaign display names for a unified keyword row (comma-ready order). */
export function resolveUnifiedCampaignNames(
  row: UnifiedKeywordRow,
  campaignNameById: Map<string, string>
): string[] {
  const ids = uniqueNonEmpty(row.sources.map((s) => s.campaign_id));
  return ids.map((id) => resolveCampaignDisplayName(id, campaignNameById) || id);
}

/** Resolve campaign display name when a single campaign id is present. */
export function resolveUnifiedCampaignLabel(
  row: UnifiedKeywordRow,
  campaignNameById: Map<string, string>
): string {
  const names = resolveUnifiedCampaignNames(row, campaignNameById);
  if (names.length === 0) return '-';
  return names.join(', ');
}

export type KeywordCampaignFilterOption = {
  id: string;
  name: string;
};

export function unifiedKeywordMatchesCampaign(
  row: UnifiedKeywordRow,
  campaignId: string
): boolean {
  const target = coerceDisplayString(campaignId);
  if (!target || target === 'ALL') return true;
  return row.sources.some((source) => coerceDisplayString(source.campaign_id) === target);
}

/** Client-side campaign filter; narrows multi-campaign unified rows to the selected campaign. */
export function filterUnifiedKeywordsByCampaign(
  rows: UnifiedKeywordRow[],
  campaignId: string
): UnifiedKeywordRow[] {
  const target = coerceDisplayString(campaignId);
  if (!target || target === 'ALL') return rows;

  const filtered: UnifiedKeywordRow[] = [];
  for (const row of rows) {
    const matchingSources = row.sources.filter(
      (source) => coerceDisplayString(source.campaign_id) === target
    );
    if (matchingSources.length === 0) continue;
    if (matchingSources.length === row.sources.length) {
      filtered.push(row);
      continue;
    }
    const [textPart, matchPart] = row.groupKey.split('|');
    filtered.push(
      buildUnifiedRow(
        row.groupKey,
        textPart || row.keyword_text,
        matchPart || row.match_type,
        matchingSources
      )
    );
  }
  return filtered;
}

export function buildKeywordCampaignFilterOptions(
  campaigns: Array<{ campaignId: string; name: string }>,
  keywordRows: UnifiedKeywordRow[],
  campaignNameById: Map<string, string>
): KeywordCampaignFilterOption[] {
  const byId = new Map<string, string>();
  for (const campaign of campaigns) {
    const id = coerceDisplayString(campaign.campaignId);
    if (!id) continue;
    byId.set(id, coerceDisplayString(campaign.name) || id);
  }
  for (const row of keywordRows) {
    for (const source of row.sources) {
      const id = coerceDisplayString(source.campaign_id);
      if (!id || byId.has(id)) continue;
      byId.set(id, resolveCampaignDisplayName(id, campaignNameById) || id);
    }
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}
